import React, { FC, useRef, useState } from 'react';
import { Box, Skeleton, SxProps, Theme } from '@mui/material';
import { FileVariantsEnum, UserDto } from '@sweatpals/shared';
import { MobileUserProfileMenu } from './mobile-user-profile-menu.component';
import { clsx } from '../../../utils';
import { Colors } from '../../../constants';
import {
	AddEventIcon,
	EventSearchFilled,
	EventSearchOutlined,
	TicketFilled,
	TicketOutlined,
	UserFilled,
	UserOutlined,
} from '../../../icons';
import { UserAvatar } from '../../user-avatar';
import { mobileSystemFooterStyles } from './mobile-system-footer.styles';

export interface MobileSystemFooterProps {
	currentUser?: UserDto | null;
	isEventSearchPage?: boolean;
	isPurchasesPage?: boolean;
	isLoginPage?: boolean;
	isLoading?: boolean;
	sx?: SxProps<Theme>;

	getPublicFileUrl(fileId: string, variant?: FileVariantsEnum): string;
	openEventSearchPage?(search?: string): void;
	openPurchasesPage?(): void;
	openCreateEventPage?(): void;
	openDashboardPage?(): void;
	openDownloadAppPage?(): void;
	openCreateCommunityPage?(): void;
	openLoginPage?(): void;
	handleLogout?(): void;
	openBookDemoPage?(): void;
}

export const MobileSystemFooter: FC<MobileSystemFooterProps> = props => {
	const {
		currentUser,
		isLoading = false,
		isPurchasesPage = false,
		isEventSearchPage = false,
		isLoginPage = false,
		sx,
		getPublicFileUrl,
		openPurchasesPage,
		openEventSearchPage,
		openCreateEventPage,
		openLoginPage,
		openDashboardPage,
		openDownloadAppPage,
		openCreateCommunityPage,
		handleLogout,
		openBookDemoPage,
	} = props;
	const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
	const ref = useRef<HTMLDivElement | null>(null);

	return (
		<Box sx={clsx(mobileSystemFooterStyles, isUserMenuOpen ? { backgroundColor: Colors.black } : undefined, sx)}>
			<Box
				component={isEventSearchPage && !isUserMenuOpen ? EventSearchFilled : EventSearchOutlined}
				color={isEventSearchPage && !isUserMenuOpen ? Colors.white : Colors['737373']}
				onClick={() => {
					if (isEventSearchPage) {
						return;
					}

					openEventSearchPage?.();
				}}
			/>
			<Box
				component={isPurchasesPage && !isUserMenuOpen ? TicketFilled : TicketOutlined}
				color={isPurchasesPage && !isUserMenuOpen ? Colors.white : Colors['737373']}
				onClick={() => {
					if (isPurchasesPage) {
						return;
					}

					openPurchasesPage?.();
				}}
			/>
			<Box component={AddEventIcon} color={Colors['737373']} onClick={openCreateEventPage} />
			{isLoading ? (
				<Skeleton
					sx={{
						height: '32px',
						width: '32px',
						backgroundColor: Colors.white_a15,
					}}
					variant="circular"
				/>
			) : currentUser ? (
				<Box
					ref={ref}
					onClick={() => {
						setIsUserMenuOpen(!isUserMenuOpen);
					}}
				>
					<UserAvatar
						fullName={currentUser.fullName}
						avatarSrc={currentUser.avatarId && getPublicFileUrl(currentUser.avatarId, FileVariantsEnum.XS)}
						emptyAvatarVariant={currentUser.emptyAvatarVariant}
						sx={{
							width: '32px',
							height: '32px',
							borderWidth: isUserMenuOpen ? 1 : 0,
							borderStyle: 'solid',
							borderColor: Colors.white,
						}}
					/>
				</Box>
			) : (
				<Box
					component={isLoginPage ? UserFilled : UserOutlined}
					color={isLoginPage ? Colors.white : Colors['737373']}
					onClick={openLoginPage}
				/>
			)}
			<MobileUserProfileMenu
				open={isUserMenuOpen}
				anchorEl={ref.current}
				currentUser={currentUser}
				handleLogout={handleLogout}
				getPublicFileUrl={getPublicFileUrl}
				openDownloadAppPage={openDownloadAppPage}
				openCreateCommunityPage={openCreateCommunityPage}
				openDashboardPage={openDashboardPage}
				openCreateEventPage={openCreateEventPage}
				openBookDemoPage={openBookDemoPage}
			/>
		</Box>
	);
};
